import Header from '@/components/Header';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Camera, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalysisDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
    } catch (error) {
      toast.error('Failed to load analysis');
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
        <Header />
        <div className="flex items-center justify-center pt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const isSnapshot = analysis.mode === 'snapshot';
  const result = analysis.result_data;

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="analysis-detail-page">
      <Header />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/history')}
            data-testid="back-button"
            variant="outline"
            className="border-[#0891b2] hover:bg-[#06748c] text-white bg-[#0891b2] hover:bg-[#06748c] text-white text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#0891b2]">
              {isSnapshot ? 'Snapshot Analysis' : 'Daily Tracking Analysis'}
            </h1>
            <p className="text-gray-600">{formatDate(analysis.created_at)}</p>
          </div>
        </div>

        <Card className="glass-card border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              {isSnapshot ? <Camera className="w-5 h-5 text-[#0891b2]" /> : <TrendingUp className="w-5 h-5 text-blue-400" />}
              <span>Farm Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Species</p>
                <p className="text-gray-200">{analysis.species}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="text-gray-200">{analysis.farm_location}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Genetic Origin</p>
                <p className="text-gray-200">{analysis.genetic_origin}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isSnapshot ? (
          <div className="space-y-6">
            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-[#0891b2]">Analysis Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-gray-600 mb-1">FCR Gap</p>
                    <p className="text-2xl font-bold text-[#0891b2]">
                      {result.analysis.fcr_gap > 0 ? '+' : ''}{result.analysis.fcr_gap}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-gray-600 mb-1">FCR Ratio</p>
                    <p className="text-2xl font-bold text-[#0891b2]">
                      {result.analysis.fcr_ratio}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-gray-600 mb-1">Fish Difference</p>
                    <p className="text-2xl font-bold text-[#0891b2]">
                      {result.analysis.fish_difference > 0 ? '+' : ''}{result.analysis.fish_difference}
                    </p>
                  </div>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="font-semibold mb-2">Interpretation</p>
                  <p className="text-gray-700">{result.analysis.interpretation}</p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="font-semibold mb-3">Recommendations</p>
                  <ul className="space-y-2">
                    {result.recommendations.map((rec, idx) => {
                      // Parse recommendation text and convert URLs to clickable links
                      const urlRegex = /(https?:\/\/[^\s]+)/g;
                      const parts = rec.split(urlRegex);
                      
                      return (
                        <li key={idx} className="flex items-start space-x-2">
                          <span className="text-[#0891b2] mt-1">•</span>
                          <span className="text-gray-700">
                            {parts.map((part, i) => {
                              if (part.match(urlRegex)) {
                                return (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#0891b2] underline hover:text-[#06748c]"
                                  >
                                    {part}
                                  </a>
                                );
                              }
                              return part;
                            })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-[#0891b2]">FCR Trend Chart</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={result.per_day_analysis}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(34, 211, 238, 0.1)" />
                      <XAxis dataKey="date" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0f2847', border: '1px solid rgba(34, 211, 238, 0.3)', borderRadius: '8px' }}
                        labelStyle={{ color: '#22d3ee' }}
                      />
                      <Legend />
                      <ReferenceLine y={1} stroke="#10b981" strokeDasharray="3 3" label="Expected" />
                      <Line type="monotone" dataKey="fcr_ratio" stroke="#22d3ee" strokeWidth={2} name="FCR Ratio" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-slate-200">
              <CardHeader>
                <CardTitle className="text-[#0891b2]">Trend Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="font-semibold mb-2">Overall Pattern</p>
                  <p className="text-gray-700">{result.trend_summary.overall_pattern}</p>
                </div>
                
                {result.trend_summary.suspected_missing_fish_periods.length > 0 && (
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-400 mb-2">Suspected Missing Fish Periods</p>
                        <div className="flex flex-wrap gap-2">
                          {result.trend_summary.suspected_missing_fish_periods.map((period, idx) => (
                            <span key={idx} className="px-2 py-1 bg-red-500/20 rounded text-sm">
                              {period}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="font-semibold mb-3">Recommendations</p>
                  <ul className="space-y-2">
                    {result.trend_summary.recommendations.map((rec, idx) => {
                      // Parse recommendation text and convert URLs to clickable links
                      const urlRegex = /(https?:\/\/[^\s]+)/g;
                      const parts = rec.split(urlRegex);
                      
                      return (
                        <li key={idx} className="flex items-start space-x-2">
                          <span className="text-[#0891b2] mt-1">•</span>
                          <span className="text-gray-700">
                            {parts.map((part, i) => {
                              if (part.match(urlRegex)) {
                                return (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#0891b2] underline hover:text-[#06748c]"
                                  >
                                    {part}
                                  </a>
                                );
                              }
                              return part;
                            })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}